import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ZoomIn, ZoomOut, RotateCcw, X, Maximize2, Move, Sparkles } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { getProductImageProps } from '../../utils/imageOptimizer';

interface ProductLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  images?: string[];
  productName: string;
}

export default function ProductLightbox({
  isOpen,
  onClose,
  imageUrl,
  images = [],
  productName
}: ProductLightboxProps) {
  // Combine single imageUrl and images array if provided
  const galleryImages = Array.from(
    new Set([imageUrl, ...images].filter((img): img is string => Boolean(img)))
  );
  
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const currentImage = galleryImages[activeImageIndex] || imageUrl;

  // Reset active image index when modal opens or primary imageUrl changes
  useEffect(() => {
    if (isOpen) {
      const idx = galleryImages.indexOf(imageUrl);
      setActiveImageIndex(idx >= 0 ? idx : 0);
    }
  }, [isOpen, imageUrl]);

  // Lock body scroll when lightbox modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.25, ease: 'easeOut' } }}
          className="fixed inset-0 z-[250] flex flex-col items-center justify-between bg-black/95 backdrop-blur-2xl select-none overflow-hidden text-white"
        >
          {/* Top Bar Navigation & Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: 0.05, duration: 0.2 }}
            className="absolute top-0 inset-x-0 h-20 bg-gradient-to-b from-black/90 via-black/50 to-transparent flex items-center justify-between px-4 sm:px-8 z-50 pointer-events-none"
          >
            <div className="flex flex-col min-w-0 pr-4">
              <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-extrabold flex items-center gap-1.5 drop-shadow-md">
                <Sparkles className="w-3 h-3 text-emerald-400 animate-pulse" />
                Raj Kirana HD Zoom
              </span>
              <h3 className="text-white font-semibold text-sm sm:text-base line-clamp-1 drop-shadow-md tracking-tight">
                {productName}
              </h3>
            </div>

            <div className="flex items-center gap-2 pointer-events-auto">
              <button
                type="button"
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white transition-all cursor-pointer border border-white/20 flex items-center justify-center backdrop-blur-md active:scale-95 group shadow-lg"
                title="Close Lightbox (Esc)"
                aria-label="Close Lightbox"
              >
                <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
              </button>
            </div>
          </motion.div>

          {/* Main Full-Screen Zoom & Pan Canvas */}
          <div className="relative w-full h-full flex-1 flex items-center justify-center overflow-hidden pt-16 pb-24">
            <TransformWrapper
              key={currentImage}
              initialScale={1}
              minScale={1}
              maxScale={8}
              centerOnInit={true}
              centerZoomedOut={true}
              limitToBounds={true}
              doubleClick={{ mode: 'zoomIn', step: 1.8 }}
              wheel={{ step: 0.12 }}
              panning={{ velocityDisabled: false }}
            >
              {({ zoomIn, zoomOut, resetTransform, state }) => {
                const scalePercent = Math.round((state?.scale || 1) * 100);
                const isZoomed = (state?.scale || 1) > 1.05;

                return (
                  <React.Fragment>
                    {/* The Interactive Zoomable Canvas */}
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isZoomed ? 'grab' : 'zoom-in'
                      }}
                      contentStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <motion.img
                        initial={{ opacity: 0, scale: 0.92, filter: 'blur(8px)' }}
                        animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                        {...getProductImageProps(currentImage, 1200, true)}
                        alt={productName}
                        className="max-w-[90vw] max-h-[75vh] sm:max-w-[85vw] sm:max-h-[82vh] object-contain select-none rounded-xl drop-shadow-[0_20px_50px_rgba(0,0,0,0.8)] shadow-2xl transition-all"
                        referrerPolicy="no-referrer"
                      />
                    </TransformComponent>

                    {/* Bottom Floating Control Bar */}
                    <motion.div 
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 30 }}
                      transition={{ delay: 0.1 }}
                      className="absolute bottom-6 inset-x-0 z-40 flex flex-col items-center gap-3 px-4 pointer-events-none"
                    >
                      {/* Optional Gallery Thumbnails Navigation */}
                      {galleryImages.length > 1 && (
                        <div className="pointer-events-auto flex items-center gap-2 bg-black/70 backdrop-blur-xl p-1.5 rounded-2xl border border-white/15 shadow-2xl overflow-x-auto max-w-full">
                          {galleryImages.map((imgUrl, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                resetTransform();
                                setActiveImageIndex(idx);
                              }}
                              className={`relative w-11 h-11 rounded-xl overflow-hidden border-2 transition-all shrink-0 ${
                                activeImageIndex === idx
                                  ? 'border-emerald-400 scale-105 shadow-md shadow-emerald-500/30 ring-2 ring-emerald-400/40'
                                  : 'border-white/20 opacity-60 hover:opacity-100 hover:border-white/50'
                              }`}
                            >
                              <img 
                                src={imgUrl} 
                                alt={`Thumbnail ${idx + 1}`} 
                                loading="lazy"
                                className="w-full h-full object-cover"
                                referrerPolicy="no-referrer"
                              />
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Zoom Control Pill Toolbar */}
                      <div className="pointer-events-auto flex items-center gap-1.5 sm:gap-2 bg-black/75 backdrop-blur-2xl px-4 py-2.5 rounded-full border border-white/20 shadow-[0_10px_40px_rgba(0,0,0,0.6)]">
                        {/* Zoom Out Button */}
                        <button
                          type="button"
                          onClick={() => zoomOut(0.5)}
                          disabled={!isZoomed}
                          className="p-2 text-white/90 hover:text-white hover:bg-white/15 rounded-full disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer active:scale-90"
                          title="Zoom Out (-)"
                          aria-label="Zoom Out"
                        >
                          <ZoomOut className="w-5 h-5" />
                        </button>

                        <div className="w-[1px] h-4 bg-white/20 mx-0.5" />

                        {/* Reset / Scale percentage indicator button */}
                        <button
                          type="button"
                          onClick={() => resetTransform()}
                          className="px-3 py-1 text-xs font-mono font-bold tracking-wider text-emerald-400 hover:text-white transition-all bg-white/10 hover:bg-white/20 rounded-full border border-white/10 flex items-center gap-1.5 active:scale-95 cursor-pointer"
                          title="Click to Reset Zoom (100%)"
                        >
                          <RotateCcw className="w-3 h-3 text-white/70" />
                          <span>{scalePercent}%</span>
                        </button>

                        <div className="w-[1px] h-4 bg-white/20 mx-0.5" />

                        {/* Zoom In Button */}
                        <button
                          type="button"
                          onClick={() => zoomIn(0.5)}
                          disabled={state?.scale >= 8}
                          className="p-2 text-white/90 hover:text-white hover:bg-white/15 rounded-full disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer active:scale-90"
                          title="Zoom In (+)"
                          aria-label="Zoom In"
                        >
                          <ZoomIn className="w-5 h-5" />
                        </button>

                        <div className="w-[1px] h-4 bg-white/20 mx-0.5" />

                        {/* Center / Reset view button */}
                        <button
                          type="button"
                          onClick={() => resetTransform()}
                          className="p-2 text-white/90 hover:text-white hover:bg-white/15 rounded-full transition-all cursor-pointer active:scale-90"
                          title="Reset View"
                          aria-label="Reset View"
                        >
                          <Maximize2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Interactive Gesture Hints */}
                      <div className="flex items-center gap-2 text-[10px] text-white/50 uppercase tracking-[0.2em] font-medium drop-shadow-md">
                        <Move className="w-3 h-3 text-white/40 animate-pulse" />
                        <span className="sm:hidden">Pinch to zoom &bull; Drag to pan &bull; Double tap</span>
                        <span className="hidden sm:inline">Scroll wheel or double-click to zoom &bull; Drag to pan</span>
                      </div>
                    </motion.div>
                  </React.Fragment>
                );
              }}
            </TransformWrapper>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
